import { useClerk, useUser } from '@clerk/clerk-react';
import { Analytics } from '@vercel/analytics/react';
import { Loader2, MessageCircle, UserIcon, X } from 'lucide-react';
import { CSSProperties, ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { AimeiChatBox } from '@/components/aimei/AimeiChatBox';
import { AppSidebar } from '@/components/common/layout/AppSidebar';
import { TourRenderer } from '@/components/common/tour/TourRenderer';
import { TourTrigger } from '@/components/common/tour/TourTrigger';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { SAMPLE_ZEE_USERS } from '@/constants/territories/dummy';
import { US_STATES } from '@/constants/territories/states';
import { HOME_TOUR_CLASSES } from '@/constants/tour/classNames';
import { SIDEBAR_WIDTH } from '@/constants/ui/sidebar';
import { useGetFeatureLocks } from '@/hooks/feature-locks/useGetFeatureLocks';
import { useGetZeeFeatureLocks } from '@/hooks/feature-locks/useGetZeeFeatureLocks';
import { useGetMyDiscovery } from '@/hooks/my-discovery/useGetMyDiscovery';
import { useGetAllStages } from '@/hooks/stages/useGetAllStages';
import { useTourManager } from '@/hooks/tours/useTourManager';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { toast } from '@/hooks/ui/use-toast';
import { getFallbackURL } from '@/lib/route';
import { getStateUserCount } from '@/lib/territories';
import { cn } from '@/lib/ui/utils';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useAimeiChatStore } from '@/stores/useAimeiChatStore';
import { useTerritoryStore } from '@/stores/useTerritoryStore';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  // custom hooks
  const { user } = useUser();
  const isMobile = useIsMobile();
  const { signOut } = useClerk();
  const { pathname } = useLocation();
  const { territories } = useTerritoryStore();
  const { isVisible: isChatBoxVisible, toggle: toggleChatBox } =
    useAimeiChatStore();

  const getPreferredStateFromTerritories = () => {
    if (!territories || territories.length === 0) return null;

    const statePreferences = territories.reduce(
      (acc, territory) => {
        const state = territory.state;
        const preferenceCount = territory.state_preference_count ?? 0;
        const photos = territory.state_preference_photos ?? [];

        if (!acc[state]) {
          acc[state] = {
            state,
            count: preferenceCount,
            photo_urls: photos,
          };
        }
        return acc;
      },
      {} as Record<
        string,
        { state: string; count: number; photo_urls: string[] }
      >
    );

    const preferredState = Object.values(statePreferences).reduce(
      (max, current) => (current.count > max.count ? current : max)
    );

    return preferredState;
  };

  const preferredState = getPreferredStateFromTerritories();
  const preferenceCount = preferredState?.count ?? 0;
  const defaultPhotos = SAMPLE_ZEE_USERS.map((user) => user.photo_url);
  const preferencePhotos = [
    ...(preferredState?.photo_urls ?? []),
    ...defaultPhotos,
  ].slice(0, 3);

  const {
    setStageList,
    setFeatureLocks,
    setDiscoveryStatus,
    setDataLoading,
    setDataLoaded,
  } = useStageStore();

  const { currentTour, currentSteps, hasTour } = useTourManager();

  const {
    result: stages,
    isError: isErrorStages,
    isLoading: isLoadingStages,
    isSuccess: isSuccessStages,
  } = useGetAllStages();

  const {
    result: featureLocks,
    isError: isErrorFeatureLocks,
    isLoading: isLoadingFeatureLocks,
    isSuccess: isSuccessFeatureLocks,
  } = useGetFeatureLocks();

  const {
    result: zeeFeatureLocks,
    isError: isErrorZeeFeatureLocks,
    isLoading: isLoadingZeeFeatureLocks,
    isSuccess: isSuccessZeeFeatureLocks,
  } = useGetZeeFeatureLocks();

  const {
    result: discoveryResult,
    isError: isErrorDiscovery,
    isLoading: isLoadingDiscovery,
    isSuccess: isSuccessDiscovery,
  } = useGetMyDiscovery();

  const isLoading =
    isLoadingStages ||
    isLoadingFeatureLocks ||
    isLoadingZeeFeatureLocks ||
    isLoadingDiscovery;
  const hasError =
    isErrorStages ||
    isErrorFeatureLocks ||
    isErrorZeeFeatureLocks ||
    isErrorDiscovery;

  // event handlers
  const handleLogOut = () => {
    window.localStorage.removeItem('access-token');
    signOut({
      redirectUrl: getFallbackURL(),
    });
  };

  const sidebarStyle = {
    '--sidebar-width': `${SIDEBAR_WIDTH}px`,
  } as CSSProperties;

  useEffect(() => {
    setDataLoading(isLoading);

    const allDataLoaded =
      !isLoading &&
      (isSuccessStages || isErrorStages) &&
      (isSuccessFeatureLocks ||
        isSuccessZeeFeatureLocks ||
        isErrorFeatureLocks ||
        isErrorZeeFeatureLocks) &&
      (isSuccessDiscovery || isErrorDiscovery);

    if (allDataLoaded) {
      setDataLoaded(true);
    }
  }, [
    isLoading,
    isSuccessStages,
    isErrorStages,
    isSuccessFeatureLocks,
    isSuccessZeeFeatureLocks,
    isErrorFeatureLocks,
    isErrorZeeFeatureLocks,
    isSuccessDiscovery,
    isErrorDiscovery,
    setDataLoading,
    setDataLoaded,
  ]);

  useEffect(() => {
    if (hasError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, [hasError]);

  useEffect(() => {
    if (isSuccessStages && stages?.data) {
      setStageList(stages.data);
    }

    if (isSuccessZeeFeatureLocks && zeeFeatureLocks?.data) {
      setFeatureLocks(zeeFeatureLocks.data);
    } else if (isSuccessFeatureLocks && featureLocks?.data) {
      setFeatureLocks(featureLocks.data);
    }

    if (isSuccessDiscovery && discoveryResult?.data) {
      setDiscoveryStatus(discoveryResult.data.discovery_status ?? undefined);
    }
  }, [
    stages,
    featureLocks,
    zeeFeatureLocks,
    discoveryResult,
    isSuccessStages,
    isSuccessFeatureLocks,
    isSuccessZeeFeatureLocks,
    isSuccessDiscovery,
    setStageList,
    setFeatureLocks,
    setDiscoveryStatus,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <SidebarProvider style={sidebarStyle}>
      <AppSidebar />
      <main className="h-screen flex-1">
        {hasTour && currentTour && (
          <TourRenderer
            steps={currentSteps}
            run={currentTour.run}
            stepIndex={currentTour.stepIndex}
            onCallback={currentTour.handleJoyrideCallback}
          />
        )}

        <header className="sticky top-0 z-10 flex min-h-16 items-center justify-between border-b border-alpha-dark-5 bg-white px-4 py-3 md:px-6">
          <SidebarTrigger className="min-[920px]:hidden" />
          {preferredState ? (
            <div
              className={cn(
                'flex items-center gap-2 rounded-full border border-border px-4 py-2 max-md:hidden',
                HOME_TOUR_CLASSES.SEARCHING_USERS
              )}
            >
              {preferencePhotos.map((photo, index) => (
                <Avatar
                  key={index}
                  className={cn('h-6 w-6', index !== 0 && 'ml-[-20px]')}
                >
                  <AvatarImage src={photo} />
                  <AvatarFallback>
                    <UserIcon className="h-3 w-3" />
                  </AvatarFallback>
                </Avatar>
              ))}
              <p className="as-strong as-title-03">
                {getStateUserCount(preferredState.state, preferenceCount)} other
                users are looking in{' '}
                {US_STATES[preferredState.state as keyof typeof US_STATES]}
              </p>
            </div>
          ) : (
            <div></div>
          )}

          <div className="flex items-center gap-3">
            {hasTour && currentTour && (
              <TourTrigger onStartTour={currentTour.startTour} />
            )}

            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className={cn(
                  buttonVariants({
                    size: 'icon',
                    variant: 'ghost',
                    className:
                      'cursor-pointer overflow-hidden rounded-full shadow-md',
                  })
                )}
              >
                <Avatar
                  className={cn('size-9', HOME_TOUR_CLASSES.PROFILE_BUTTON)}
                >
                  <AvatarImage src={user?.imageUrl} />
                  <AvatarFallback>
                    <UserIcon className="size-5" />
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mr-6 mt-2 w-[200px]">
                <Link to="/dashboard/profile?tab=general">
                  <DropdownMenuItem>
                    <p className="p-0.5">My Profile</p>
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem onClick={handleLogOut}>
                  <p className="p-0.5">Sign Out</p>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="relative min-h-[calc(100vh-61px)] p-4 max-md:w-screen md:min-h-[calc(100vh-67px)] md:p-6 min-[920px]:w-[calc(100vw-230px)]">
          {children}
          {!isMobile &&
            (pathname === '/dashboard' ||
              pathname.includes('/dashboard/discovery')) && (
              <>
                <div
                  className={cn(
                    'fixed bottom-4 left-4 right-4 max-md:bottom-20 max-md:left-4 max-md:right-4 md:bottom-20 md:right-6 min-[920px]:left-[254px]',
                    'transition-all duration-300 ease-in-out',
                    isChatBoxVisible
                      ? 'translate-y-0 opacity-100'
                      : 'pointer-events-none translate-y-full opacity-0',
                    HOME_TOUR_CLASSES.AIMEI_CHAT_BOX
                  )}
                >
                  <AimeiChatBox />
                </div>
                <button
                  onClick={toggleChatBox}
                  className={cn(
                    buttonVariants({
                      size: 'icon',
                      variant: 'default',
                      className:
                        'fixed z-20 h-12 w-12 rounded-full shadow-lg transition-all duration-200 hover:scale-105',
                    }),
                    'bottom-4 right-4 md:bottom-6 md:right-6'
                  )}
                >
                  <div className="transition-transform duration-200">
                    {isChatBoxVisible ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <MessageCircle className="h-5 w-5" />
                    )}
                  </div>
                </button>
              </>
            )}
        </div>
      </main>
      <Analytics />
    </SidebarProvider>
  );
};
